import React from 'react';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {
  IconAlertTriangle,
  IconClockHour4,
  IconHourglassHigh,
  IconLock,
} from '@tabler/icons-react';
import type {CourseAssignmentStatCardCounts} from 'src/api/ProactApiClient';
import {CourseAssignmentLatenessBucketEnum} from 'src/api/ProactApiClient';
import {StatCard} from 'src/design/molecules/StatCard';

export interface ComplianceStatCardsProps {
  counts: CourseAssignmentStatCardCounts | undefined;
  activeBucket: CourseAssignmentLatenessBucketEnum | undefined;
  onSelectBucket: (bucket: CourseAssignmentLatenessBucketEnum) => void;
}

const CARD_CONFIG: {
  bucket: CourseAssignmentLatenessBucketEnum;
  title: string;
  color: string;
  icon: React.ReactNode;
  getCount: (counts: CourseAssignmentStatCardCounts) => number;
}[] = [
  {
    bucket: CourseAssignmentLatenessBucketEnum.Overdue,
    title: 'Overdue',
    color: 'red',
    icon: <IconAlertTriangle size={18} />,
    getCount: counts => counts.overdue,
  },
  {
    bucket: CourseAssignmentLatenessBucketEnum.DueInSevenDays,
    title: 'Due in 7 days',
    color: 'orange',
    icon: <IconClockHour4 size={18} />,
    getCount: counts => counts.dueInSevenDays,
  },
  {
    bucket: CourseAssignmentLatenessBucketEnum.ExpiringInThirtyDays,
    title: 'Expiring in 30 days',
    color: 'yellow',
    icon: <IconHourglassHigh size={18} />,
    getCount: counts => counts.expiringInThirtyDays,
  },
  {
    bucket: CourseAssignmentLatenessBucketEnum.LockedOut,
    title: 'Locked out',
    color: 'gray',
    icon: <IconLock size={18} />,
    getCount: counts => counts.lockedOut,
  },
];

/**
 * The four Wave-1 Compliance Cockpit stat cards. Clicking a card toggles the
 * corresponding lateness-bucket filter on the assignments grid below.
 */
export const ComplianceStatCards: React.FC<ComplianceStatCardsProps> = ({
  counts,
  activeBucket,
  onSelectBucket,
}) => (
  <SimpleGrid cols={{base: 2, md: 4}} mb="md">
    {CARD_CONFIG.map(config => (
      <StatCard
        key={config.bucket}
        title={config.title}
        count={counts ? config.getCount(counts) : 0}
        icon={config.icon}
        color={config.color}
        isActive={activeBucket === config.bucket}
        onClick={() => onSelectBucket(config.bucket)}
      />
    ))}
  </SimpleGrid>
);
